import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import {
  buildDeliveryAttributePayload,
  useDeliveryAttributeGroups,
  type DeliveryDecision,
  type DeliverySelection,
} from '~/components/common/delivery-run-panel';
import {
  enrichmentRunPanelReducer,
  INITIAL_PANEL_STATE,
  selectedAttributeKeys,
} from '~/components/common/enrichment-run-panel';
import { GMCPreDeliveryConfirmation } from '~/components/common/gmc-pre-delivery-confirmation';
import { MetaPreDeliveryConfirmation } from '~/components/common/meta-pre-delivery-confirmation';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { useSubmitScheduledPipelineApproval } from '~/features/content-enrichment/hooks/use-content-scoring';
import type { ScheduledPipelineApprovalPayload } from '~/features/content-enrichment/types/scheduled-enrichment.types';
import { eligibleKeysFromRunConfig } from '~/features/content-enrichment/utils/delivery-eligible-keys';
import { useDeliveryRunDetail } from '~/features/deliveries/hooks/use-delivery-run-detail';
import { useTriggerDelivery } from '~/features/deliveries/hooks/use-trigger-delivery';
import type { TriggerDeliveryRequest } from '~/features/deliveries/types/delivery-runs.types';
import {
  redeliveryBlockReason,
  shouldForceRedeliver,
} from '~/features/deliveries/utils/redelivery-gate';
import { useTenantAdmin } from '~/hooks/useTenantAdmin';

import { useBatchHeader } from '../../hooks/use-batch-detail';
import { useLanguageApproval } from '../../hooks/use-language-approval';
import { RunValidationStrip } from '../batch-detail/run-validation-strip';
import { LanguageApprovalSection } from '../translations/language-approval-section';

import { DeliveryWizardDecisionStep } from './delivery-wizard-decision-step';
import { DeliveryWizardFooter } from './delivery-wizard-footer';
import { DeliveryWizardHeader } from './delivery-wizard-header';
import { DeliveryWizardPreviewStep } from './delivery-wizard-preview-step';
import { DeliveryWizardProgressStep } from './delivery-wizard-progress-step';
import { DeliveryWizardRunStep } from './delivery-wizard-run-step';
import { DeliveryWizardStepper } from './delivery-wizard-stepper';

type WizardStep = 'decision' | 'preview' | 'progress';

interface Props {
  batchId: string;
  /**
   * Retry delivery: the delivery run to redeliver from. Turns the wizard into
   * "choose attributes → review → deliver" for a batch that already
   * delivered — no approve / reject, and the last step triggers a new
   * delivery run (`POST deliveries/{cid}/deliver`) instead of the approval.
   */
  redeliverRunId?: string | undefined;
}

/**
 * Pre-delivery wizard — replaces the "Review delivery" side panel with a
 * 3-step flow: Decision (approve/reject + attribute picker), Preview
 * (pre-enrichment vs. enriched vs. proposed delivery), and Progress (live
 * delivery status, ending in a redirect back to batch details).
 *
 * Host-agnostic content — no outer page layout or `useEnrichmentContext()`
 * dependency — so it's reused verbatim by both the Catalog Operations
 * "Review" route and the Content Enrichment "Confirm & Publish" wizard
 * routes; each caller supplies its own outer layout and resolves `batchId`
 * (always the catalog-operations job UUID) however its host needs to.
 *
 * Owns the Decision step's attribute-selection state directly (rather than
 * that step owning it locally) so the shared stepper and footer — rendered
 * here, not inside the step — can reflect it live: whether Preview is
 * reachable, the live "N of M selected" summary, and reject's required-reason
 * validity all need to be visible outside the step that edits them.
 *
 * The approval-submission mutation fires at the Preview → Progress
 * transition, not Decision → Preview — Preview previews values that haven't
 * been committed yet, so nothing is sent to the server until the operator
 * confirms it. Reject has no preview to show, so it still submits
 * immediately from Step 1, same as today's side panel.
 *
 * "Confirm & deliver" opens the platform setup-instructions gate
 * (`GMCPreDeliveryConfirmation`/`MetaPreDeliveryConfirmation`) before the
 * mutation actually fires — same required acknowledgement
 * `ConfirmPublishButton` shows today, now surfaced for both entry points
 * (Catalog Operations Review previously shipped without it).
 */
export function DeliveryWizardPage({ batchId, redeliverRunId }: Props) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: header, isLoading: headerLoading } = useBatchHeader(batchId);
  // Subsidiary admins + super-admins get the Temporal-style run inspector on
  // Step 3; members keep the simple progress view (and its auto-redirect).
  const isTenantAdmin = useTenantAdmin();
  const isRedelivery = redeliverRunId !== undefined;
  // The run being retried supplies the connection and the enrichment run to redeliver.
  const sourceRun = useDeliveryRunDetail(redeliverRunId ?? '', isRedelivery, { poll: false });
  const redeliver = useTriggerDelivery(false);
  // The run a redelivery started — step 3 follows it instead of the batch's own run.
  const [redeliveredRunId, setRedeliveredRunId] = useState<string | null>(null);

  const [step, setStep] = useState<WizardStep>('decision');
  const [setupGateOpen, setSetupGateOpen] = useState(false);

  // Resume-on-load: a delivery already in flight means the operator (or a
  // prior tab) already got past Decision/Preview, so a reload must not
  // re-show the attribute picker over a run that's already mid-flight. Fires
  // once, on the header's first resolve — `header.isAwaitingApproval` stays
  // true for the whole delivery window (can't distinguish "not decided yet"
  // from "approved, delivering"), so `isDelivering` is the only reliable
  // signal. Render-time reset (not an effect) — avoids a setState-in-effect
  // cascade for a one-shot adjustment, same pattern `GMCPreDeliveryConfirmation`
  // already uses.
  const [hasResumed, setHasResumed] = useState(false);
  if (!hasResumed && !headerLoading && header) {
    setHasResumed(true);
    if (header.isDelivering && !isRedelivery) setStep('progress');
  }

  // Preview's full-screen toggle (hides the header/stepper card, drops the
  // width cap) — scoped to Preview only, so leaving the step always resets
  // it, and returning to Preview later never starts pre-expanded. Render-time
  // reset (not an effect), same pattern as `hasResumed` above — a plain
  // state variable, not a ref, since refs can't be read/written during render.
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const [prevStep, setPrevStep] = useState(step);
  if (prevStep !== step) {
    setPrevStep(step);
    if (step !== 'preview' && isPreviewExpanded) setIsPreviewExpanded(false);
  }
  const previewExpanded = isPreviewExpanded && step === 'preview';

  const submitApproval = useSubmitScheduledPipelineApproval();
  const pipelineWorkflowId = header?.pipelineWorkflowId ?? null;
  const platform = header?.sourcePlatform.toUpperCase() === 'META' ? 'meta' : 'google';
  const languages = useLanguageApproval(
    pipelineWorkflowId,
    step !== 'progress' && platform !== 'meta',
  );
  const eligibleKeys = eligibleKeysFromRunConfig(header?.runConfig);

  // Decision step's state, owned here (see class doc above) rather than in
  // the step itself.
  const {
    groups,
    isLoading: groupsLoading,
    isError: groupsError,
    refetch: refetchGroups,
  } = useDeliveryAttributeGroups(true, eligibleKeys, platform, header?.alwaysOnAttributes);
  const [attrState, dispatch] = useReducer(
    enrichmentRunPanelReducer,
    INITIAL_PANEL_STATE,
    (init) =>
      groups.length > 0 ? enrichmentRunPanelReducer(init, { type: 'HYDRATE', groups }) : init,
  );
  // Re-seed selection when the taxonomy resolves after first paint (cold
  // cache) or when `eligibleKeys` narrows the set. HYDRATE no-ops on an
  // unchanged group set, so a background refetch can't wipe a live selection.
  useEffect(() => {
    if (groups.length > 0) dispatch({ type: 'HYDRATE', groups });
  }, [groups]);
  const [decision, setDecision] = useState<DeliveryDecision>('APPROVED');
  const [notes, setNotes] = useState('');

  const selectedKeys = selectedAttributeKeys(attrState);
  const availableKeys = groups.flatMap((g) => g.attributes.map((a) => a.key));
  const hasAttributes = groups.length > 0;
  const approveReady = !hasAttributes || selectedKeys.length > 0;
  const canContinueToPreview = decision === 'APPROVED' && approveReady;
  const reasonValid = notes.trim().length > 0;

  function backToBatchDetails() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
    void navigate({ to: '/catalog-operations/$batchId', params: { batchId } });
  }

  function submit(payload: ScheduledPipelineApprovalPayload, onSuccess: () => void) {
    if (!pipelineWorkflowId) {
      toast.error('Unable to resolve the delivery pipeline for this batch. Please reload.');
      return;
    }
    submitApproval.mutate({ workflowId: pipelineWorkflowId, payload }, { onSuccess });
  }

  function handleRejectSubmit() {
    submit({ decision: 'REJECTED', ...(notes.trim() ? { notes: notes.trim() } : {}) }, () => {
      toast.success('Delivery rejected. Nothing was sent.');
      backToBatchDetails();
    });
  }

  /**
   * Retry delivery: send the chosen attributes on a new delivery run. Mirrors
   * what workflow-service sends after an approval — `contentAttributes` become
   * `deliveryFields` (both platforms), plus the descriptor toggles and the
   * approved translations (GMC only).
   */
  function handleRedeliver(selection: DeliverySelection) {
    const run = sourceRun.data;
    if (!run?.connectionId) {
      toast.error(t('deliveryWizard.redeliver.runMissing'));
      return;
    }
    const attributes = buildDeliveryAttributePayload(selection);
    const locales = (languages.payload.approvedLocales ?? []).filter((l) => l !== 'en-US');
    const workflowId = run.source?.sourceWorkflowId;
    const body: TriggerDeliveryRequest = {
      // Full re-push only from a cleanly completed run; from a failed / partial run
      // the retry resumes (delivered-and-unchanged products are skipped).
      forceRedeliver: shouldForceRedeliver(run),
      ...(workflowId ? { workflowId } : {}),
      ...(attributes.contentAttributes.length > 0
        ? { deliveryFields: attributes.contentAttributes }
        : {}),
      ...(attributes.searchDescriptors
        ? {
            searchDescriptors: {
              enabled: attributes.searchDescriptors.enabled,
              phraseDescriptions: attributes.searchDescriptors.phraseDescriptions,
            },
          }
        : {}),
      ...(platform === 'google' && locales.length > 0 ? { targetLocales: locales } : {}),
      enrichmentJobId: batchId,
    };
    redeliver.mutate(
      { connectionId: run.connectionId, body },
      {
        onSuccess: (job) => {
          setRedeliveredRunId(job.id);
          setStep('progress');
        },
      },
    );
  }

  function handleConfirmDelivery() {
    const selection: DeliverySelection = {
      decision: 'APPROVED',
      notes: notes.trim(),
      attributeKeys: selectedKeys,
      availableKeys,
    };
    if (isRedelivery) {
      handleRedeliver(selection);
      return;
    }
    const attributes = selectedKeys.length > 0 ? buildDeliveryAttributePayload(selection) : {};
    submit(
      {
        decision: 'APPROVED',
        ...(selection.notes ? { notes: selection.notes } : {}),
        ...attributes,
        ...(platform === 'google' ? languages.payload : {}),
      },
      () => {
        setStep('progress');
      },
    );
  }

  // Retry delivery gate — the same rules the Retry delivery button checks before
  // navigating (it shows a popup there). Reaching here blocked means a deep link
  // or a state that moved since the click: send the operator back to batch
  // details with the reason as a toast rather than a dead-end screen.
  const redeliverBlockedBody = ((): string | null => {
    // While this operator's own redelivery is being created, a header refetch can already
    // show it as a live delivery — that's not a block, it's the run we're about to follow.
    if (
      !isRedelivery ||
      step === 'progress' ||
      redeliver.isPending ||
      headerLoading ||
      sourceRun.isLoading
    ) {
      return null;
    }
    if (sourceRun.isError || !sourceRun.data) return t('deliveryWizard.redeliver.runMissing');
    if (!header) return t('deliveryWizard.redeliver.blocked.batchMissing');
    const reason = redeliveryBlockReason({ isTenantAdmin, run: sourceRun.data, batch: header });
    if (reason === 'membersOnly') return t('deliveryWizard.redeliver.membersOnly');
    return reason ? t(`deliveryWizard.redeliver.blocked.${reason}`) : null;
  })();
  useEffect(() => {
    if (!redeliverBlockedBody) return;
    toast.error(redeliverBlockedBody);
    void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
    void navigate({ to: '/catalog-operations/$batchId', params: { batchId } });
  }, [redeliverBlockedBody, queryClient, navigate, batchId]);

  if (headerLoading) {
    return (
      <div className="space-y-4" data-testid="delivery-wizard-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  // Redelivery waits for the run it retries; a refused deep link is already
  // bouncing back to batch details (effect above), so hold on a skeleton.
  if (isRedelivery && step !== 'progress' && (sourceRun.isLoading || redeliverBlockedBody)) {
    return (
      <div className="space-y-4" data-testid="delivery-wizard-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  // Once on Progress this session, stay there even after the batch leaves the
  // awaiting/delivering window (admins keep inspecting a finished run).
  if (
    !header ||
    (!isRedelivery && step !== 'progress' && !header.isAwaitingApproval && !header.isDelivering)
  ) {
    return (
      <div
        className="mx-auto flex max-w-lg flex-1 flex-col items-center justify-center gap-3 text-center"
        data-testid="delivery-wizard-blocked"
      >
        <h2 className="text-foreground text-lg font-semibold">
          {t('deliveryWizard.blocked.title')}
        </h2>
        <p className="text-muted-foreground text-sm">{t('deliveryWizard.blocked.body')}</p>
        <Button
          variant="link"
          className="cursor-pointer"
          onClick={backToBatchDetails}
          data-testid="delivery-wizard-blocked-back-button"
        >
          {t('deliveryWizard.blocked.backToBatch')}
        </Button>
      </div>
    );
  }

  const previewSelection: DeliverySelection = {
    decision: 'APPROVED',
    notes: notes.trim(),
    attributeKeys: selectedKeys,
    availableKeys,
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col" data-testid="delivery-wizard-page">
      {/* Pinned header + stepper — matches the batch detail page's pinned-
          header/scrolling-body split (`catalog-batch-detail-page.tsx`). One
          bordered card holds breadcrumb + title (left) and the stepper
          (right) in a single row when there's room, wrapping the stepper to
          its own row otherwise — it needs ~51rem so step hints never wrap or
          clip, to keep the pinned chrome short. Hidden while Preview is full-screen — the
          toggle's whole point is reclaiming this space. */}
      {!previewExpanded && (
        <div className="shrink-0 pb-6">
          <div
            className="bg-background flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-2xl border px-6 py-4"
            data-testid="delivery-wizard-header"
          >
            <div className="shrink-0">
              <DeliveryWizardHeader header={header} batchId={batchId} isRedelivery={isRedelivery} />
            </div>
            <div className="max-w-5xl min-w-[min(100%,51rem)] flex-1">
              <DeliveryWizardStepper
                step={step}
                canContinueToPreview={canContinueToPreview}
                approvedAttributeCount={selectedKeys.length}
                destinationLabel={header.source}
                isRedelivery={isRedelivery}
                onGoTo={(key) => {
                  setStep(key);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="scrollbar-transparent flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* Validation before the go / no-go: the same strip as batch detail, with its
            "Fix automatically" — not once the delivery is running. */}
        {step !== 'progress' && !previewExpanded && (
          <RunValidationStrip target={header} className="shrink-0 pb-4" />
        )}
        {step === 'decision' && (
          <DeliveryWizardDecisionStep
            groups={groups}
            isLoading={groupsLoading}
            isError={groupsError}
            onRetry={refetchGroups}
            state={attrState}
            dispatch={dispatch}
            decision={decision}
            onDecisionChange={setDecision}
            notes={notes}
            onNotesChange={setNotes}
            isRedelivery={isRedelivery}
            {...(platform === 'google'
              ? {
                  extraSection: (
                    <LanguageApprovalSection
                      options={languages.options}
                      selected={languages.selected}
                      onToggle={languages.toggle}
                      disabled={submitApproval.isPending || redeliver.isPending}
                    />
                  ),
                }
              : {})}
          />
        )}
        {step === 'preview' && (
          <DeliveryWizardPreviewStep
            batchId={batchId}
            curationRefId={header.curationRefId}
            platform={platform}
            enrichmentConnectorCode={header.useCase === 'META_ADS' ? 'META' : undefined}
            selection={previewSelection}
            groups={groups}
            attrState={attrState}
            dispatch={dispatch}
            languageOptions={languages.options}
            selectedLanguages={languages.selected}
            batchWorkflowId={header.workflowId}
            isExpanded={previewExpanded}
            onExpandedChange={setIsPreviewExpanded}
          />
        )}
        {step === 'progress' &&
          (isTenantAdmin ? (
            <DeliveryWizardRunStep
              batchId={batchId}
              jobId={header.batchId}
              onBackToBatch={backToBatchDetails}
              runId={redeliveredRunId}
            />
          ) : (
            <DeliveryWizardProgressStep jobId={header.batchId} onComplete={backToBatchDetails} />
          ))}
      </div>

      {step !== 'progress' && (
        <DeliveryWizardFooter
          step={step}
          decision={decision}
          selectedCount={selectedKeys.length}
          totalCount={availableKeys.length}
          reasonValid={reasonValid}
          isSubmittingReject={submitApproval.isPending}
          // The whole batch's product count, not the Preview step's own
          // (search-scoped) rail total — the footer describes "everything
          // that will be delivered," not "what currently matches the panel's
          // in-progress search."
          productCount={header.productsCuratedTotal ?? 0}
          // Names the delivery target (the merchant catalog: GMC / Meta
          // Commerce), not the ads source `header.source` shows elsewhere.
          destinationLabel={t(`deliveryWizard.footer.preview.destination.${platform}`)}
          onCancel={backToBatchDetails}
          onContinue={() => {
            setStep('preview');
          }}
          onRejectSubmit={handleRejectSubmit}
          onBack={() => {
            setStep('decision');
          }}
          onConfirmDeliver={() => {
            setSetupGateOpen(true);
          }}
          isConfirming={submitApproval.isPending || redeliver.isPending}
        />
      )}

      {platform === 'google' ? (
        <GMCPreDeliveryConfirmation
          open={setupGateOpen}
          onCancel={() => {
            setSetupGateOpen(false);
          }}
          onConfirm={() => {
            setSetupGateOpen(false);
            handleConfirmDelivery();
          }}
        />
      ) : (
        <MetaPreDeliveryConfirmation
          open={setupGateOpen}
          onCancel={() => {
            setSetupGateOpen(false);
          }}
          onConfirm={() => {
            setSetupGateOpen(false);
            handleConfirmDelivery();
          }}
        />
      )}
    </div>
  );
}
